import { TypedDocumentNode, gql } from '@apollo/client';
import { IntlShape } from 'react-intl';

import { withFragments } from '@sorare/core/src/lib/gql';

import { Errors } from 'components/so5/ComposeTeam/Context';
import { HANDLED_RULES } from 'lib/so5';

import { formatRules_so5Leaderboard } from './__generated__/formatRules.graphql';
import { getActiveClubsRule } from './helpers/getActiveClubsRule';
import { getAgeRule } from './helpers/getAgeRule';
import { getAllowLegendRule } from './helpers/getAllowLegend';
import { getAtLeastOfClubsRule } from './helpers/getAtLeastOfClubsRule';
import { getAtLeastOfCompetitionsRule } from './helpers/getAtLeastOfCompetitionsRule';
import { getAverageScoresRule } from './helpers/getAverageScoresRule';
import getCaptainRule from './helpers/getCaptainRule';
import { getCardBonusRule } from './helpers/getCardBonusRule';
import { getCardEditionsCountRule } from './helpers/getCardEditionsCountRule';
import { getCompetitionsRule } from './helpers/getCompetitionsRule';
import { getInternationalCompetitionsRule } from './helpers/getInternationalCompetitionsRule';
import { getLeaguesRule } from './helpers/getLeaguesRule';
import { getMaximumPlayersAverageScoreRule } from './helpers/getMaximumPlayersAverageScoreRule';
import { getMinimumPlayersAverageScoreRule } from './helpers/getMinimumPlayersAverageScoreRule';
import { getNationalitiesRule } from './helpers/getNationalitiesRule';
import { getNotDomesticCompetitionsRule } from './helpers/getNotDomesticCompetitionsRule';
import getNotNationalitiesRule from './helpers/getNotNationalitiesRule';
import { getRarityLimitsRule } from './helpers/getRarityLimitsRule';
import { getSameActiveClubRule } from './helpers/getSameActiveClubRule';
import { getSameNationalityRule } from './helpers/getSameNationalityRule';
import { getSeasonBonusRule } from './helpers/getSeasonBonusRule';
import { getSeasonsRule } from './helpers/getSeasonsRule';
import { getSerialNumberRule } from './helpers/getSerialNumberRule';
import { getSumOfAverageScoresRule } from './helpers/getSumOfAverageScoresRule';
import { FormatRule } from './types';

export function getErrorFrom(errors: Errors, id: string) {
  if (!errors) {
    return '';
  }
  return (
    errors
      ?.filter(({ path }) => path?.[1].toLowerCase() === id.toLowerCase())
      .map(({ message }) => message)[0] || ''
  );
}

export const formatRules = withFragments(
  (
    so5Leaderboard: formatRules_so5Leaderboard,
    errors: Errors,
    intl: IntlShape,
    ftue?: boolean
  ) => {
    const { displayedRules } = so5Leaderboard || {};
    if (!displayedRules) {
      return [];
    }
    return [
      ...HANDLED_RULES.flatMap((rule): FormatRule | FormatRule[] | null => {
        const error = getErrorFrom(errors, rule);
        switch (rule) {
          case 'seasons':
            return getSeasonsRule(displayedRules, error);
          case 'leagues':
            return getLeaguesRule(displayedRules, error);
          case 'competitions':
            return getCompetitionsRule(displayedRules, error);
          case 'internationalCompetitions':
            return getInternationalCompetitionsRule(displayedRules, error);
          case 'notDomesticCompetitions':
            return getNotDomesticCompetitionsRule(displayedRules, error);
          case 'activeClubs':
            return getActiveClubsRule(displayedRules, error);
          case 'sameNationality':
            return getSameNationalityRule(displayedRules, error);
          case 'serialNumber':
            return getSerialNumberRule(displayedRules, error);
          case 'nationalitiesCount':
            return getNationalitiesRule(displayedRules, error);
          case 'notNationalities':
            return getNotNationalitiesRule(displayedRules, error);
          case 'atLeastOfCompetitions':
            return getAtLeastOfCompetitionsRule(displayedRules, error);
          case 'atLeastOfClubs':
            return getAtLeastOfClubsRule(displayedRules, error);
          case 'age':
            return getAgeRule(displayedRules, error);
          case 'captainRarities':
            return getCaptainRule(so5Leaderboard, error, !!ftue);
          case 'sameActiveClub':
            return getSameActiveClubRule(displayedRules, error);
          case 'seasonBonusForSpecificTeams':
            return getSeasonBonusRule(
              displayedRules,
              error || getErrorFrom(errors, 'SeasonBonus') // needed until we migrate seasonBonus to correct type
            );
          case 'minimumPlayersAverageScore':
            return getMinimumPlayersAverageScoreRule(displayedRules, error);
          case 'maximumPlayersAverageScore':
            return getMaximumPlayersAverageScoreRule(displayedRules, error);
          case 'rarityLimits':
            return getRarityLimitsRule(
              so5Leaderboard,
              error || getErrorFrom(errors, 'Scarcity'),
              !!ftue
            );
          case 'allowLegend':
            return getAllowLegendRule(displayedRules, error);
          case 'cardEditionsCount': {
            return getCardEditionsCountRule(displayedRules, error);
          }
          case 'sumOfAverageScores':
            return getSumOfAverageScoresRule(displayedRules, error);
          case 'averageScores':
            return getAverageScoresRule(displayedRules, error);
          default:
            return [];
        }
      }),
      getCardBonusRule(so5Leaderboard),
    ].filter(Boolean);
  },
  {
    so5Leaderboard: gql`
      fragment formatRules_so5Leaderboard on So5Leaderboard {
        slug
        displayedRules {
          id
          ...getSeasonsRule_so5Rules
          ...getCompetitionsRule_so5Rules
          ...getLeaguesRule_so5Rules
          ...getInternationalCompetitionsRule_so5Rules
          ...getNotDomesticCompetitionsRule_so5Rules
          ...getActiveClubsRule_so5Rules
          ...getSameNationalityRule_so5Rules
          ...getSerialNumberRule_so5Rules
          ...getNationalitiesRule_so5Rules
          ...getNotNationalitiesRule_so5Rules
          ...getAtLeastOfCompetitionsRule_so5Rules
          ...getAtLeastOfClubsRule_so5Rules
          ...getAgeRule_so5Rules
          ...getSameActiveClubRule_so5Rules
          ...getSeasonBonusRule_so5Rules
          ...getMinimumPlayersAverageScoreRule_so5Rules
          ...getMaximumPlayersAverageScoreRule_so5Rules
          ...getCardEditionsCountRule_so5Rules
          ...getAllowLegendRule_so5Rules
          ...getSumOfAverageScoresRule_so5Rules
          ...getAverageScoresRule_so5Rules
        }
        ...getRarityLimitsRule_so5Leaderboard
        ...getCaptainRule_so5Leaderboard
        ...getCardBonusRule_so5Leaderboard
      }
      ${getSeasonsRule.fragments.so5Rules}
      ${getCompetitionsRule.fragments.so5Rules}
      ${getLeaguesRule.fragments.so5Rules}
      ${getInternationalCompetitionsRule.fragments.so5Rules}
      ${getNotDomesticCompetitionsRule.fragments.so5Rules}
      ${getActiveClubsRule.fragments.so5Rules}
      ${getSameNationalityRule.fragments.so5Rules}
      ${getSerialNumberRule.fragments.so5Rules}
      ${getNationalitiesRule.fragments.so5Rules}
      ${getNotNationalitiesRule.fragments.so5Rules}
      ${getAtLeastOfCompetitionsRule.fragments.so5Rules}
      ${getAtLeastOfClubsRule.fragments.so5Rules}
      ${getAgeRule.fragments.so5Rules}
      ${getSameActiveClubRule.fragments.so5Rules}
      ${getSeasonBonusRule.fragments.so5Rules}
      ${getMinimumPlayersAverageScoreRule.fragments.so5Rules}
      ${getMaximumPlayersAverageScoreRule.fragments.so5Rules}
      ${getCardEditionsCountRule.fragments.so5Rules}
      ${getAllowLegendRule.fragments.so5Rules}
      ${getSumOfAverageScoresRule.fragments.so5Rules}
      ${getAverageScoresRule.fragments.so5Rules}
      ${getRarityLimitsRule.fragments.so5Leaderboard}
      ${getCaptainRule.fragments.so5Leaderboard}
      ${getCardBonusRule.fragments.so5Leaderboard}
    ` as TypedDocumentNode<formatRules_so5Leaderboard>,
  }
);
